import { Notification } from "@nova/schemas"
import {
    AnyEventPayload,
    NotificationContext,
    NotificationVariable,
    notificationVariableCatalog,
    notificationVariables,
    ResolvedVariable,
} from "./variables"
import { isNotifiableEventKind, NotifiableEventKind, VariablesFor } from "./registry"

export type CopyRegistry<Copy> = {
    [K in NotifiableEventKind]: (
        text: Record<VariablesFor<K>, string>,
        notification: Extract<Notification, { kind: K }>
    ) => Copy
}

const variableValue = (
    variable: NotificationVariable,
    data: AnyEventPayload,
    context: NotificationContext
) => {
    const definition = notificationVariableCatalog[variable]
    if (definition.source === "resolved") return context[variable as ResolvedVariable]
    const derive = definition.derive as (data: AnyEventPayload) => string | undefined
    return definition.supportedBy(data) ? derive(data) : undefined
}

const variableValues = (data: AnyEventPayload, context: NotificationContext) =>
    Object.fromEntries(
        notificationVariables.map((variable) => [
            variable,
            variableValue(variable, data, context) ??
                notificationVariableCatalog[variable].fallback,
        ])
    ) as Record<NotificationVariable, string>

export const buildCopy = <Copy>(
    registry: CopyRegistry<Copy>,
    notification: Notification,
    context: NotificationContext
) => {
    if (!isNotifiableEventKind(notification.kind)) return undefined
    const build = registry[notification.kind] as (
        text: Record<NotificationVariable, string>,
        notification: Notification
    ) => Copy
    return build(variableValues(notification.data, context), notification)
}
